import type {GetProp, SelectProps} from "antd";
import {DatePicker, Empty, Select, Spin, Table, Tooltip} from "antd";
import {ColumnsType} from "antd/es/table";
import dayjs, {Dayjs} from "dayjs";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link, useSearchParams} from "react-router-dom";
import {VisAxis, VisBulletLegend, VisCrosshair, VisStackedBar, VisTooltip, VisXYContainer} from "@unovis/react";
import {AppContextContext, OrganizationServiceContext, SourceUpdatesServiceContext, VulSourceServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import SourceUpdatesAttributeBreakdownRow from "../../domain/SourceUpdatesAttributeBreakdownRow";
import SourceUpdatesBreakdownRow from "../../domain/SourceUpdatesBreakdownRow";
import SourceUpdatesDailyPoint from "../../domain/SourceUpdatesDailyPoint";
import SourceUpdatesSummary from "../../domain/SourceUpdatesSummary";
import VulSource from "../../domain/VulSource";
import {Permission} from "../../domain/auth/Permission";
import {formatVulSourceType, VulSourceType} from "../../domain/VulSourceType";
import QueryOptions from "../../sal-ui/QueryOptions";
import {FormPersister} from "../../sal-ui/FormPersister";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import {DateRangePresetId, parseFilterDate, resolveDateRangePresetValue} from "../../utils/DateRangePresets";
import {createDateRangePickerPresets, takePendingDatePresetId} from "../../utils/DateRangePresetPicker";
import {formatCompactInteger, formatInteger} from "../../utils/FormatUtils";
import {orderSystemAndOrganizationOptionGroups} from "../../utils/SelectOptionUtils";
import * as styles from "./SourceUpdatesOverview.module.css";

const DAY_MS = 86_400_000;
const RECENT_ATTRIBUTES_CHANGED_TOOLTIP = "Items with changed attributes where Published is at most one year old.";
const PERSISTENT_IDENT = "SourceUpdatesOverview";

const updateTypeLegendItems = [
    {name: "New items", color: "#1677ff"},
    {name: "JSON changed", color: "#95de64"},
    {name: "Attributes changed", color: "#ffc53d"},
];

interface ChangedItemListQueryOptions {
    sourceId?: string;
    fromFilterName: string;
    toFilterName: string;
    from: Dayjs;
    to: Dayjs;
    sourcePublishedFrom?: Dayjs;
}

interface SourceUpdatesFilterValues {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    dateRangePresetId?: DateRangePresetId;
    organizationId?: string;
    sourceId?: string;
}

type SourceSelectOptions = GetProp<SelectProps, "options">;

function SourceUpdatesOverview() {
    const appContext = useContext(AppContextContext);
    const sourceUpdatesService = useContext(SourceUpdatesServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const systemAdmin = appContext.user.isSystemAdmin();
    const canReadSystemSources = appContext.user.hasPermission(Permission.SOURCE__READ_SEAL);
    const [initialFilterValues] = useState(() =>
        loadSourceUpdatesFilterValues(searchParams, filterFormPersister, systemAdmin)
    );
    const [summary, setSummary] = useState<SourceUpdatesSummary>();
    const [daily, setDaily] = useState<SourceUpdatesDailyPoint[]>([]);
    const [breakdown, setBreakdown] = useState<SourceUpdatesBreakdownRow[]>([]);
    const [attributeBreakdown, setAttributeBreakdown] = useState<SourceUpdatesAttributeBreakdownRow[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [sources, setSources] = useState<VulSource[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(initialFilterValues.organizationId);
    const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>(initialFilterValues.sourceId);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState<[Dayjs, Dayjs]>([initialFilterValues.dateFrom, initialFilterValues.dateTo]);
    const [dateRangePresetId, setDateRangePresetId] = useState<DateRangePresetId | undefined>(initialFilterValues.dateRangePresetId);
    const pendingDatePresetIdRef = useRef<DateRangePresetId>();
    const dateFromStr = range[0].format("YYYY-MM-DD");
    const dateToStr = range[1].format("YYYY-MM-DD");
    const loaded = !loading && summary !== undefined;
    const hasData = loaded && (summary.affectedItemCount > 0 || daily.length > 0 || breakdown.length > 0);

    useEffect(() => {
        if (systemAdmin) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(result => setOrganizations(result.data));
        }

        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setSources(result.data));
    }, []);

    useEffect(() => {
        const filterValues = {
            dateFrom: range[0],
            dateTo: range[1],
            dateRangePresetId,
            organizationId: systemAdmin ? selectedOrgId : undefined,
            sourceId: selectedSourceId
        };
        const params: Record<string, string> = dateRangePresetId
            ? {range: dateRangePresetId}
            : {from: dateFromStr, to: dateToStr};

        if (filterValues.organizationId) {
            params.organizationId = filterValues.organizationId;
        }

        if (filterValues.sourceId) {
            params.sourceId = filterValues.sourceId;
        }

        filterFormPersister.saveState(filterValues);
        setSearchParams(params, {replace: true});
        reload();
    }, [selectedOrgId, selectedSourceId, dateFromStr, dateToStr, dateRangePresetId]);

    return (
        <Spin spinning={loading}>
            <div className={styles.filters}>
                <DatePicker.RangePicker
                    allowClear={false}
                    value={range}
                    onChange={value => {
                        const pendingDatePresetId = takePendingDatePresetId(pendingDatePresetIdRef);

                        if (value?.[0] && value?.[1]) {
                            setRange([value[0], value[1]]);
                            setDateRangePresetId(pendingDatePresetId);
                        }
                    }}
                    presets={createDateRangePickerPresets(dateRangePresetId, pendingDatePresetIdRef, (presetId, presetRange) => {
                        setRange(presetRange);
                        setDateRangePresetId(presetId);
                    })}
                />
                {systemAdmin && organizations.length > 0 && (
                    <Select
                        allowClear
                        placeholder="All organizations"
                        value={selectedOrgId}
                        onChange={setSelectedOrgId}
                        style={{minWidth: 220}}
                        showSearch
                        optionFilterProp="label"
                        options={organizations.map(o => ({value: o.id!, label: o.name!}))}
                    />
                )}
                {sources.length > 0 && (
                    <Select
                        allowClear
                        placeholder="All sources"
                        value={selectedSourceId}
                        onChange={setSelectedSourceId}
                        style={{minWidth: 260}}
                        showSearch
                        optionFilterProp="label"
                        options={buildSourceSelectOptions()}
                    />
                )}
            </div>

            {loaded && !hasData && (
                <Empty description="No source updates data found for the selected period." style={{margin: "48px 0"}}/>
            )}

            {hasData && (
                <>
                    <div className={styles.summaryGroups}>
                        {summaryCards().map(card => (
                            <div key={card.key} className={styles.summaryCard}>
                                <div className={styles.summaryLabel}>{card.label}</div>
                                <div className={styles.summaryValue} title={card.title}>{card.value}</div>
                            </div>
                        ))}
                    </div>

                    {daily.length > 1 && (
                        <div className={styles.section}>
                            <div className={styles.chartHeader}>
                                <h3>Trend</h3>
                                <div className={styles.chartControls}>
                                    <VisBulletLegend items={updateTypeLegendItems}/>
                                </div>
                            </div>
                            <div className={styles.chartContainer}>
                                {renderTrendChart()}
                            </div>
                        </div>
                    )}

                    <div className={styles.section}>
                        <h3>Daily</h3>
                        <Table
                            rowKey="date"
                            pagination={false}
                            size="small"
                            showSorterTooltip={false}
                            dataSource={daily}
                            columns={dailyColumns()}
                        />
                    </div>

                    <div className={styles.section}>
                        <h3>By source</h3>
                        <Table
                            rowKey={row => row.sourceId || row.label}
                            pagination={false}
                            size="small"
                            showSorterTooltip={false}
                            dataSource={breakdown}
                            columns={sourceColumns()}
                            summary={renderBreakdownSummary}
                        />
                    </div>

                    {attributeBreakdown.length > 0 && (
                        <div className={styles.section}>
                            <h3>Attribute changes</h3>
                            <Table
                                rowKey="attributeName"
                                pagination={false}
                                size="small"
                                showSorterTooltip={false}
                                dataSource={attributeBreakdown}
                                columns={attributeBreakdownColumns()}
                            />
                        </div>
                    )}
                </>
            )}
        </Spin>
    );

    function reload() {
        setLoading(true);

        const query = buildSourceUpdatesQuery();

        Promise.all([
            sourceUpdatesService.getSummary(query),
            sourceUpdatesService.getDaily(query),
            sourceUpdatesService.getBreakdown(query),
            sourceUpdatesService.getAttributeBreakdown(query),
        ])
            .then(([summaryResult, dailyResult, breakdownResult, attributeBreakdownResult]) => {
                setSummary(summaryResult);
                setDaily(dailyResult);
                setBreakdown(breakdownResult);
                setAttributeBreakdown(attributeBreakdownResult);
            })
            .finally(() => setLoading(false));
    }

    function buildSourceUpdatesQuery() {
        return {
            dateFrom: dateFromStr,
            dateTo: dateToStr,
            organizationId: systemAdmin ? selectedOrgId : undefined,
            sourceId: selectedSourceId
        };
    }

    function buildSourceSelectOptions(): SourceSelectOptions {
        const systemSources = canReadSystemSources ? sources.filter(source => source.owner === ResourceOwnerType.SYSTEM) : [];
        const orgGroups = new Map<string, VulSource[]>();

        sources
            .filter(source => source.owner === ResourceOwnerType.ORGANIZATION)
            .forEach(source => {
                const orgName = source.organization?.name || "Unknown";
                const group = orgGroups.get(orgName);

                if (group) {
                    group.push(source);
                } else {
                    orgGroups.set(orgName, [source]);
                }
            });

        const systemOptionGroups = systemSources.length > 0 ? [{
            label: "System",
            options: systemSources.map(source => ({label: source.name || "", value: source.id || ""}))
        }] : [];
        const organizationOptionGroups = [...orgGroups.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([orgName, orgSources]) => ({
                label: orgName,
                options: orgSources.map(source => ({label: source.name || "", value: source.id || ""}))
            }));

        return orderSystemAndOrganizationOptionGroups(systemOptionGroups, organizationOptionGroups, systemAdmin);
    }

    function compactTickFormat(tick: number) {
        return formatCompactInteger(tick);
    }

    function tooltipRow(color: string, label: string, value: string) {
        return `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">` +
            `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};flex-shrink:0"></span>` +
            `<span style="flex:1">${label}</span>` +
            `<span style="font-weight:500;margin-left:12px">${value}</span></div>`;
    }

    function tooltipWrap(date: string, rows: string, total: string) {
        return `<div style="font-size:12px;padding:6px 10px;min-width:170px">` +
            `<div style="font-weight:600;margin-bottom:4px;border-bottom:1px solid #f0f0f0;padding-bottom:4px">${date}</div>` +
            rows +
            `<div style="font-weight:600;margin-top:4px;border-top:1px solid #f0f0f0;padding-top:4px;text-align:right">${total}</div>` +
            `</div>`;
    }

    function renderTrendChart() {
        const colors = updateTypeLegendItems.map(item => item.color);
        const labels = updateTypeLegendItems.map(item => item.name);

        return (
            <VisXYContainer<SourceUpdatesDailyPoint> data={daily} height={250}>
                <VisStackedBar<SourceUpdatesDailyPoint>
                    x={(d: SourceUpdatesDailyPoint) => new Date(d.date).getTime()}
                    y={[
                        (d: SourceUpdatesDailyPoint) => d.newItemCount,
                        (d: SourceUpdatesDailyPoint) => d.jsonDataChangedItemCount,
                        (d: SourceUpdatesDailyPoint) => d.attributesChangedItemCount,
                    ]}
                    color={(_: SourceUpdatesDailyPoint, i: number) => colors[i]}
                    dataStep={DAY_MS}
                    barPadding={0.2}
                    roundedCorners={2}
                />
                <VisCrosshair<SourceUpdatesDailyPoint>
                    template={(d: SourceUpdatesDailyPoint) => {
                        const values = [
                            d.newItemCount,
                            d.jsonDataChangedItemCount,
                            d.attributesChangedItemCount,
                        ];
                        const rows = labels
                            .map((label, i) => tooltipRow(colors[i], label, formatInteger(values[i])))
                            .join("");

                        return tooltipWrap(dayjs(d.date).format("YYYY-MM-DD"), rows, "Affected items: " + formatInteger(d.affectedItemCount));
                    }}
                />
                <VisTooltip/>
                <VisAxis<SourceUpdatesDailyPoint>
                    type="x"
                    tickFormat={(tick: number) => dayjs(tick).format("MMM D")}
                    numTicks={Math.min(daily.length, 10)}
                />
                <VisAxis<SourceUpdatesDailyPoint>
                    type="y"
                    tickFormat={compactTickFormat}
                />
            </VisXYContainer>
        );
    }

    function summaryCards() {
        return [
            {
                key: "newItemCount",
                label: "New items",
                value: formatCompactInteger(summary?.newItemCount ?? 0),
                title: formatInteger(summary?.newItemCount ?? 0)
            },
            {
                key: "jsonDataChangedItemCount",
                label: "JSON changed",
                value: formatCompactInteger(summary?.jsonDataChangedItemCount ?? 0),
                title: formatInteger(summary?.jsonDataChangedItemCount ?? 0)
            },
            {
                key: "attributesChangedItemCount",
                label: "Attributes changed",
                value: formatCompactInteger(summary?.attributesChangedItemCount ?? 0),
                title: formatInteger(summary?.attributesChangedItemCount ?? 0)
            },
            {
                key: "recentAttributesChangedItemCount",
                label: "Attributes changed (recent)",
                value: formatCompactInteger(summary?.recentAttributesChangedItemCount ?? 0),
                title: formatInteger(summary?.recentAttributesChangedItemCount ?? 0)
            },
            {
                key: "affectedItemCount",
                label: "Affected items",
                value: formatCompactInteger(summary?.affectedItemCount ?? 0),
                title: formatInteger(summary?.affectedItemCount ?? 0)
            },
        ];
    }

    function dailyColumns(): ColumnsType<SourceUpdatesDailyPoint> {
        return [
            {
                dataIndex: "date",
                title: "Date",
                width: 160,
                defaultSortOrder: "ascend",
                sortDirections: ascendingFirstSortDirections,
                render: value => dayjs(value).format("YYYY-MM-DD"),
                sorter: (a, b) => a.date.localeCompare(b.date)
            },
            {
                dataIndex: "newItemCount",
                title: "New items",
                align: "right",
                render: value => formatInteger(value),
                sorter: (a, b) => a.newItemCount - b.newItemCount
            },
            {
                dataIndex: "jsonDataChangedItemCount",
                title: "JSON changed",
                align: "right",
                render: (value, row) => renderDailyChangedCount(value, row, "dataChangedFrom", "dataChangedTo"),
                sorter: (a, b) => a.jsonDataChangedItemCount - b.jsonDataChangedItemCount
            },
            {
                title: "Attributes changed",
                align: "center",
                children: [
                    {
                        dataIndex: "attributesChangedItemCount",
                        title: "All times",
                        align: "right",
                        render: (value, row) => renderDailyChangedCount(value, row, "attributesChangedFrom", "attributesChangedTo"),
                        sorter: (a, b) => a.attributesChangedItemCount - b.attributesChangedItemCount
                    },
                    {
                        dataIndex: "recentAttributesChangedItemCount",
                        title: <Tooltip title={RECENT_ATTRIBUTES_CHANGED_TOOLTIP}>Recent</Tooltip>,
                        align: "right",
                        render: (value, row) => renderRecentDailyAttributesChangedCount(value, row),
                        sorter: (a, b) => a.recentAttributesChangedItemCount - b.recentAttributesChangedItemCount
                    },
                ]
            },
            {
                dataIndex: "affectedItemCount",
                title: "Affected items",
                align: "right",
                defaultSortOrder: "descend",
                render: value => formatInteger(value),
                sorter: (a, b) => a.affectedItemCount - b.affectedItemCount
            },
        ];
    }

    function sourceColumns(): ColumnsType<SourceUpdatesBreakdownRow> {
        return [
            {
                dataIndex: "label",
                title: "Source",
                render: (value, row) => row.sourceId ? <Link to={`/vul-sources/${row.sourceId}`}>{value}</Link> : value,
                sorter: (a, b) => compareText(a.label, b.label)
            },
            {
                dataIndex: "sourceType",
                title: "Type",
                render: value => formatVulSourceType(value as VulSourceType),
                sorter: (a, b) => compareText(formatVulSourceType(a.sourceType as VulSourceType), formatVulSourceType(b.sourceType as VulSourceType))
            },
            {
                dataIndex: "organizationName",
                title: "Organization",
                render: (value, row) => row.organizationId ? <Link to={`/organizations/${row.organizationId}`}>{value}</Link> : "System",
                sorter: (a, b) => compareText(a.organizationName ?? "System", b.organizationName ?? "System")
            },
            {
                dataIndex: "newItemCount",
                title: "New items",
                align: "right",
                render: value => formatInteger(value),
                sorter: (a, b) => a.newItemCount - b.newItemCount
            },
            {
                dataIndex: "jsonDataChangedItemCount",
                title: "JSON changed",
                align: "right",
                render: (value, row) => renderSourceChangedCount(value, row, "dataChangedFrom", "dataChangedTo"),
                sorter: (a, b) => a.jsonDataChangedItemCount - b.jsonDataChangedItemCount
            },
            {
                title: "Attributes changed",
                align: "center",
                children: [
                    {
                        dataIndex: "attributesChangedItemCount",
                        title: "All times",
                        align: "right",
                        render: (value, row) => renderSourceChangedCount(value, row, "attributesChangedFrom", "attributesChangedTo"),
                        sorter: (a, b) => a.attributesChangedItemCount - b.attributesChangedItemCount
                    },
                    {
                        dataIndex: "recentAttributesChangedItemCount",
                        title: <Tooltip title={RECENT_ATTRIBUTES_CHANGED_TOOLTIP}>Recent</Tooltip>,
                        align: "right",
                        render: (value, row) => renderRecentSourceAttributesChangedCount(value, row),
                        sorter: (a, b) => a.recentAttributesChangedItemCount - b.recentAttributesChangedItemCount
                    },
                ]
            },
            {
                dataIndex: "affectedItemCount",
                title: "Affected items",
                align: "right",
                defaultSortOrder: "descend",
                render: value => formatInteger(value),
                sorter: (a, b) => a.affectedItemCount - b.affectedItemCount
            },
        ];
    }

    function attributeBreakdownColumns(): ColumnsType<SourceUpdatesAttributeBreakdownRow> {
        return [
            {
                dataIndex: "attributeName",
                title: "Attribute",
                sorter: (a, b) => compareText(a.attributeName, b.attributeName)
            },
            {
                dataIndex: "itemCount",
                title: <Tooltip title="Unique vulnerability items where this attribute changed.">Items</Tooltip>,
                align: "right",
                defaultSortOrder: "descend",
                render: value => formatInteger(value),
                sorter: (a, b) => a.itemCount - b.itemCount
            },
            {
                dataIndex: "changeCount",
                title: <Tooltip title="Attribute-change log entries for this attribute. One item can contribute multiple changes.">Changes</Tooltip>,
                align: "right",
                render: (value, row) => renderAttributeChangesLink(value, row),
                sorter: (a, b) => a.changeCount - b.changeCount
            },
            {
                dataIndex: "recentItemCount",
                title: <Tooltip title={RECENT_ATTRIBUTES_CHANGED_TOOLTIP}>Recent items</Tooltip>,
                align: "right",
                render: value => formatInteger(value),
                sorter: (a, b) => a.recentItemCount - b.recentItemCount
            },
        ];
    }

    function renderAttributeChangesLink(value: number, row: SourceUpdatesAttributeBreakdownRow) {
        if (value === 0) {
            return formatInteger(value);
        }

        return <Link to={`/attribute-changes?${attributeChangesQueryString(row.attributeName)}`}>{formatInteger(value)}</Link>;
    }

    function attributeChangesQueryString(attributeName: string) {
        const params = new URLSearchParams();
        const filter = {
            typeHints: {},
            values: {
                changedRange: [dateFromStr, dateToStr],
                attributeNames: [attributeName],
                sourceIds: selectedSourceId ? [selectedSourceId] : [],
                organizationId: systemAdmin ? selectedOrgId : undefined
            }
        };

        params.set("filter", FormPersister.encodeUrlState(filter));

        return params.toString();
    }

    function renderBreakdownSummary(data: readonly SourceUpdatesBreakdownRow[]) {
        if (data.length === 0) {
            return null;
        }

        const totals = data.reduce((acc, row) => ({
            newItemCount: acc.newItemCount + row.newItemCount,
            jsonDataChangedItemCount: acc.jsonDataChangedItemCount + row.jsonDataChangedItemCount,
            attributesChangedItemCount: acc.attributesChangedItemCount + row.attributesChangedItemCount,
            recentAttributesChangedItemCount: acc.recentAttributesChangedItemCount + row.recentAttributesChangedItemCount,
            affectedItemCount: acc.affectedItemCount + row.affectedItemCount,
        }), {
            newItemCount: 0,
            jsonDataChangedItemCount: 0,
            attributesChangedItemCount: 0,
            recentAttributesChangedItemCount: 0,
            affectedItemCount: 0
        });

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><strong>{formatInteger(totals.newItemCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><strong>{formatInteger(totals.jsonDataChangedItemCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right"><strong>{formatInteger(totals.attributesChangedItemCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right"><strong>{formatInteger(totals.recentAttributesChangedItemCount)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right"><strong>{formatInteger(totals.affectedItemCount)}</strong></Table.Summary.Cell>
            </Table.Summary.Row>
        );
    }

    function renderDailyChangedCount(value: number, row: SourceUpdatesDailyPoint, fromFilterName: string, toFilterName: string) {
        const date = dayjs(row.date);

        return renderChangedCountLink(value, createChangedItemListQueryString({
            sourceId: selectedSourceId,
            fromFilterName,
            toFilterName,
            from: date,
            to: date
        }));
    }

    function renderRecentDailyAttributesChangedCount(value: number, row: SourceUpdatesDailyPoint) {
        const date = dayjs(row.date);

        return renderChangedCountLink(value, createChangedItemListQueryString({
            sourceId: selectedSourceId,
            fromFilterName: "attributesChangedFrom",
            toFilterName: "attributesChangedTo",
            from: date,
            to: date,
            sourcePublishedFrom: sourcePublishedOneYearAgo()
        }));
    }

    function renderSourceChangedCount(value: number, row: SourceUpdatesBreakdownRow, fromFilterName: string, toFilterName: string) {
        if (!row.sourceId) {
            return formatInteger(value);
        }

        return renderChangedCountLink(value, createChangedItemListQueryString({
            sourceId: row.sourceId,
            fromFilterName,
            toFilterName,
            from: range[0],
            to: range[1]
        }));
    }

    function renderRecentSourceAttributesChangedCount(value: number, row: SourceUpdatesBreakdownRow) {
        if (!row.sourceId) {
            return formatInteger(value);
        }

        return renderChangedCountLink(value, createChangedItemListQueryString({
            sourceId: row.sourceId,
            fromFilterName: "attributesChangedFrom",
            toFilterName: "attributesChangedTo",
            from: range[0],
            to: range[1],
            sourcePublishedFrom: sourcePublishedOneYearAgo()
        }));
    }

    function renderChangedCountLink(value: number, queryString: string) {
        if (value === 0) {
            return formatInteger(value);
        }

        return (
            <Link to={`/vul-source-items?${queryString}`} target="_blank" rel="noopener noreferrer">
                {formatInteger(value)}
            </Link>
        );
    }

    function createChangedItemListQueryString({sourceId, fromFilterName, toFilterName, from, to, sourcePublishedFrom}: ChangedItemListQueryOptions) {
        const filter = {
            typeHints: {
                [fromFilterName]: "dayjs",
                [toFilterName]: "dayjs",
                ...(sourcePublishedFrom ? {sourcePublishedFrom: "dayjs"} : {}),
            },
            values: {
                ...(sourceId ? {sources: [sourceId]} : {}),
                [fromFilterName]: from.startOf("day").toISOString(),
                [toFilterName]: to.endOf("day").toISOString(),
                ...(sourcePublishedFrom ? {sourcePublishedFrom: sourcePublishedFrom.startOf("day").toISOString()} : {}),
            }
        };

        const searchParams = new URLSearchParams();

        searchParams.set("filter", FormPersister.encodeUrlState(filter));
        searchParams.set("$readOnly", "");

        return searchParams.toString();
    }

    function sourcePublishedOneYearAgo() {
        return dayjs().subtract(1, "year");
    }

    function compareText(a?: string, b?: string) {
        return (a ?? "").localeCompare(b ?? "");
    }
}

function loadSourceUpdatesFilterValues(searchParams: URLSearchParams, filterFormPersister: FormPersister, systemAdmin: boolean): SourceUpdatesFilterValues {
    if (hasSourceUpdatesFilterSearchParams(searchParams, systemAdmin)) {
        return sourceUpdatesFilterValuesFromSearchParams(searchParams, systemAdmin);
    }

    return normalizeSourceUpdatesFilterValues(filterFormPersister.loadValues(), systemAdmin);
}

function hasSourceUpdatesFilterSearchParams(searchParams: URLSearchParams, systemAdmin: boolean) {
    return searchParams.has("range")
        || searchParams.has("from")
        || searchParams.has("to")
        || searchParams.has("sourceId")
        || (systemAdmin && searchParams.has("organizationId"));
}

function sourceUpdatesFilterValuesFromSearchParams(searchParams: URLSearchParams, systemAdmin: boolean): SourceUpdatesFilterValues {
    const defaults = createDefaultSourceUpdatesFilterValues();
    const presetId = normalizeDateRangePresetId(searchParams.get("range"));
    const presetRange = resolveDateRangePresetValue(presetId);

    return {
        dateFrom: presetRange?.[0] ?? parseFilterDate(searchParams.get("from"), defaults.dateFrom),
        dateTo: presetRange?.[1] ?? parseFilterDate(searchParams.get("to"), defaults.dateTo),
        dateRangePresetId: presetRange ? presetId : undefined,
        organizationId: systemAdmin ? searchParams.get("organizationId") ?? undefined : undefined,
        sourceId: searchParams.get("sourceId") ?? undefined
    };
}

function normalizeSourceUpdatesFilterValues(values: Partial<SourceUpdatesFilterValues>, systemAdmin: boolean): SourceUpdatesFilterValues {
    const defaults = createDefaultSourceUpdatesFilterValues();
    const presetId = normalizeDateRangePresetId(values.dateRangePresetId);
    const presetRange = resolveDateRangePresetValue(presetId);

    return {
        dateFrom: presetRange?.[0] ?? parseFilterDate(values.dateFrom, defaults.dateFrom),
        dateTo: presetRange?.[1] ?? parseFilterDate(values.dateTo, defaults.dateTo),
        dateRangePresetId: presetRange ? presetId : undefined,
        organizationId: systemAdmin ? values.organizationId : undefined,
        sourceId: values.sourceId
    };
}

function createDefaultSourceUpdatesFilterValues(): SourceUpdatesFilterValues {
    const dateRangePresetId = "last-7-days";
    const range = resolveDateRangePresetValue(dateRangePresetId)!;

    return {
        dateFrom: range[0],
        dateTo: range[1],
        dateRangePresetId
    };
}

function normalizeDateRangePresetId(value: unknown): DateRangePresetId | undefined {
    return resolveDateRangePresetValue(value) ? value as DateRangePresetId : undefined;
}

export default SourceUpdatesOverview;
